/**
 * Weekly Splash Screen Overlay
 * Shows weekly activity summary or getting-started tips for greenfield users
 * Fetches data from GET /api/user/splash
 */

import React, { useEffect, useState } from 'react';
import { Card, Badge, Button, Spinner } from 'flowbite-react';
import { userApi } from '../api/user';
import {
  HiArrowRight,
  HiLightBulb,
  HiShieldCheck,
  HiExclamation,
  HiSparkles,
  HiClock,
  HiCollection,
  HiCheckCircle,
} from 'react-icons/hi';

interface SplashSummary {
  sessions_count: number;
  total_duration_minutes: number;
  standards_injected: number;
  standards_followed: number;
  violations_detected: number;
  active_projects: number;
  patterns_harvested: number;
  week_start: string;
  week_end: string;
}

interface SplashData {
  show_splash: boolean;
  summary: SplashSummary;
  is_greenfield: boolean;
  tips: string[];
}

interface WeeklySplashProps {
  onDismiss: () => void;
}

const WeeklySplash: React.FC<WeeklySplashProps> = ({ onDismiss }) => {
  const [splashData, setSplashData] = useState<SplashData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSplash = async () => {
      setLoading(true);
      const response = await userApi.getSplash();

      if (cancelled) return;

      if (response.success && response.data) {
        const payload = (response.data as any).data || response.data;
        setSplashData(payload);

        if (!payload.show_splash) {
          onDismiss();
        }
      } else {
        setError(response.error || 'Failed to load weekly summary');
      }

      setLoading(false);
    };

    fetchSplash();

    return () => { cancelled = true; };
  }, [onDismiss]);

  const handleDismiss = async () => {
    if (!splashData) return;

    setAcknowledging(true);

    await userApi.acknowledgeSplash(splashData.summary.week_start);

    setAcknowledging(false);
    onDismiss();
  };

  const formatDateRange = (start: string, end: string): string => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <Card className="w-full max-w-md mx-4">
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      </div>
    );
  }

  if (error || !splashData || !splashData.show_splash) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4">
        {splashData.is_greenfield ? (
          <GreenfieldContent tips={splashData.tips} onDismiss={handleDismiss} acknowledging={acknowledging} />
        ) : (
          <ActivityContent
            summary={splashData.summary}
            formatDateRange={formatDateRange}
            formatDuration={formatDuration}
            onDismiss={handleDismiss}
            acknowledging={acknowledging}
          />
        )}
      </Card>
    </div>
  );
};

interface GreenfieldContentProps {
  tips: string[];
  onDismiss: () => void;
  acknowledging: boolean;
}

const GreenfieldContent: React.FC<GreenfieldContentProps> = ({ tips, onDismiss, acknowledging }) => (
  <div className="space-y-4">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
        <HiSparkles className="h-6 w-6 text-blue-600" />
      </div>
      <h2 className="text-xl font-bold text-gray-900">Welcome to MindMeld</h2>
      <p className="mt-1 text-sm text-gray-500">
        Here are some tips to get started with intelligent standards injection.
      </p>
    </div>

    <div className="space-y-3">
      {tips.map((tip, index) => (
        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 mt-0.5">
            <HiLightBulb className="h-5 w-5 text-amber-500" />
          </div>
          <p className="text-sm text-gray-700">{tip}</p>
        </div>
      ))}
    </div>

    <div className="flex justify-end pt-2">
      <Button onClick={onDismiss} disabled={acknowledging}>
        {acknowledging ? <Spinner size="sm" className="mr-2" /> : null}
        Get Started
        <HiArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  </div>
);

interface ActivityContentProps {
  summary: SplashSummary;
  formatDateRange: (start: string, end: string) => string;
  formatDuration: (minutes: number) => string;
  onDismiss: () => void;
  acknowledging: boolean;
}

const ActivityContent: React.FC<ActivityContentProps> = ({
  summary,
  formatDateRange,
  formatDuration,
  onDismiss,
  acknowledging,
}) => (
  <div className="space-y-4">
    <div className="text-center">
      <h2 className="text-xl font-bold text-gray-900">Weekly Summary</h2>
      <p className="mt-1 text-sm text-gray-500">
        {formatDateRange(summary.week_start, summary.week_end)}
      </p>
    </div>

    <div className="grid grid-cols-3 gap-3">
      <StatCard
        label="Sessions"
        value={summary.sessions_count}
        icon={<HiCollection className="h-5 w-5 text-blue-600" />}
        badgeColor="info"
      />
      <StatCard
        label="Duration"
        value={formatDuration(summary.total_duration_minutes)}
        icon={<HiClock className="h-5 w-5 text-indigo-600" />}
        badgeColor="purple"
        isText
      />
      <StatCard
        label="Projects"
        value={summary.active_projects}
        icon={<HiSparkles className="h-5 w-5 text-amber-600" />}
        badgeColor="warning"
      />
    </div>

    <div className="grid grid-cols-3 gap-3">
      <StatCard
        label="Injected"
        value={summary.standards_injected}
        icon={<HiShieldCheck className="h-5 w-5 text-blue-600" />}
        badgeColor="info"
      />
      <StatCard
        label="Followed"
        value={summary.standards_followed}
        icon={<HiCheckCircle className="h-5 w-5 text-green-600" />}
        badgeColor="success"
      />
      <StatCard
        label="Violations"
        value={summary.violations_detected}
        icon={<HiExclamation className="h-5 w-5 text-red-600" />}
        badgeColor={summary.violations_detected > 0 ? 'failure' : 'success'}
      />
    </div>

    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
      <span className="text-sm text-gray-500">
        {summary.patterns_harvested > 0
          ? `${summary.patterns_harvested} pattern${summary.patterns_harvested !== 1 ? 's' : ''} harvested`
          : 'No new patterns this week'}
      </span>
      <Button onClick={onDismiss} disabled={acknowledging}>
        {acknowledging ? <Spinner size="sm" className="mr-2" /> : null}
        Dismiss
      </Button>
    </div>
  </div>
);

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  badgeColor: string;
  isText?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, badgeColor, isText }) => (
  <div className="p-3 bg-gray-50 rounded-lg">
    <div className="flex items-center justify-between mb-1">
      {icon}
      <Badge color={badgeColor}>{isText ? value : value}</Badge>
    </div>
    <p className="text-xs font-medium text-gray-600 mt-2">{label}</p>
  </div>
);

export default WeeklySplash;
